"use client";

import React, { useState } from "react";
import { motion } from "framer-motion";
import type { IFilter } from "@/src/shared/features/search";
import Hairline from "@/src/features/listing/components/ConfirmPay/Hairline";
import { useLockBodyScroll } from "@/src/shared/hooks/useLockBodyScroll";

interface IChangeGuestsSheetProps {
  guests: IFilter["guests"];
  max: { adults: number; children: number; infants: number };
  onSave: (guests: IFilter["guests"]) => void;
  onClose: () => void;
}

const StepperButton: React.FC<{
  icon: "minus" | "plus";
  label: string;
  isEnabled: boolean;
  onClick: () => void;
}> = ({ icon, label, isEnabled, onClick }) => (
  <button
    type="button"
    aria-label={label}
    disabled={!isEnabled}
    onClick={onClick}
    className="flex h-8 w-8 items-center justify-center rounded-full border border-[#DDDDDD] disabled:opacity-35"
  >
    <img
      src={`/icons/confirm-pay/stepper_${icon}.svg`}
      alt=""
      className="h-4 w-4"
    />
  </button>
);

const StepperRow: React.FC<{
  label: string;
  ageHint: string;
  count: number;
  canDecrease: boolean;
  canIncrease: boolean;
  onChange: (delta: number) => void;
}> = ({ label, ageHint, count, canDecrease, canIncrease, onChange }) => (
  <div className="flex items-center justify-between py-4">
    <div>
      <p className="text-16 font-medium leading-6 text-ink">{label}</p>
      <p className="mt-0.5 text-16 leading-5 text-ink-secondary">{ageHint}</p>
    </div>
    <div className="flex items-center gap-3">
      <StepperButton
        icon="minus"
        label={`Fewer ${label.toLowerCase()}`}
        isEnabled={canDecrease}
        onClick={() => onChange(-1)}
      />
      <span className="w-6 text-center text-16 font-medium leading-6 text-ink">
        {count}
      </span>
      <StepperButton
        icon="plus"
        label={`More ${label.toLowerCase()}`}
        isEnabled={canIncrease}
        onClick={() => onChange(1)}
      />
    </div>
  </div>
);

/** The app's `ChangeGuestsSheet`: a draft of the guests, kept only on Save. */
const ChangeGuestsSheet: React.FC<IChangeGuestsSheetProps> = ({
  guests,
  max,
  onSave,
  onClose,
}) => {
  useLockBodyScroll();
  const [draft, setDraft] = useState(guests);
  const change = (key: "adults" | "children" | "infants", delta: number) =>
    setDraft((prev) => ({ ...prev, [key]: prev[key] + delta }));

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 z-[55] bg-black/40 md:hidden"
      />
      <motion.div
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        exit={{ y: "100%" }}
        transition={{ type: "spring", damping: 30, stiffness: 300 }}
        role="dialog"
        aria-modal
        aria-label="Change guests"
        className="fixed inset-x-0 bottom-0 z-[60] rounded-t-3xl bg-white pb-[env(safe-area-inset-bottom)] md:hidden"
      >
        <div className="flex justify-end px-4 pb-2 pt-4">
          <button type="button" onClick={onClose} aria-label="Close">
            <img
              src="/icons/confirm-pay/change_guests_close.svg"
              alt=""
              className="h-6 w-6"
            />
          </button>
        </div>
        <div className="px-4 pb-4">
          <h2 className="text-22 font-semibold leading-7 text-ink">
            Change guests
          </h2>
          {max.adults > 0 && (
            <p className="mt-4 text-16 font-medium leading-6 text-ink-secondary">
              This place has a maximum of {max.adults}{" "}
              {max.adults === 1 ? "guest" : "guests"}, not including infants.
            </p>
          )}
          <div className="mt-4">
            <StepperRow
              label="Adults"
              ageHint="Age 13+"
              count={draft.adults}
              canDecrease={draft.adults > 1}
              canIncrease={draft.adults < max.adults}
              onChange={(delta) => change("adults", delta)}
            />
            <StepperRow
              label="Children"
              ageHint="Ages 2 – 12"
              count={draft.children}
              canDecrease={draft.children > 0}
              canIncrease={draft.children < max.children}
              onChange={(delta) => change("children", delta)}
            />
            <StepperRow
              label="Infants"
              ageHint="Under 2"
              count={draft.infants}
              canDecrease={draft.infants > 0}
              canIncrease={draft.infants < max.infants}
              onChange={(delta) => change("infants", delta)}
            />
          </div>
        </div>
        <Hairline />
        <div className="flex items-center justify-between p-4">
          <button
            type="button"
            onClick={onClose}
            className="h-11 rounded-full px-6 text-14 font-semibold text-ink"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSave(draft)}
            className="h-11 rounded-full bg-[#222222] px-6 text-14 font-semibold text-white"
          >
            Save
          </button>
        </div>
      </motion.div>
    </>
  );
};

export default ChangeGuestsSheet;
